import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Home, ArrowLeft } from 'lucide-react';
import { fadeInUp, staggerContainer } from '@/lib/animations';

export default function NotFoundPage() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_not_found', {
        page_path: pathname,
        page_location: window.location.href,
      });
    }
  }, [pathname]);

  return (
    <div data-testid="not-found-page" className="pt-24 min-h-screen bg-brand-black flex items-center">
      <Helmet>
        <title>Page Not Found — custum.ai</title>
        <meta name="description" content="The page you're looking for doesn't exist." />
      </Helmet>
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-24 text-center">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.p variants={fadeInUp} className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-blue-light mb-4">
            ERROR 404
          </motion.p>
          <motion.h1 variants={fadeInUp} className="text-6xl sm:text-8xl font-extrabold text-brand-white tracking-tight mb-6">
            Page Not <span className="text-brand-blue">Found</span>
          </motion.h1>
          <motion.p variants={fadeInUp} className="text-base text-brand-muted leading-relaxed mb-10 max-w-md mx-auto">
            The page you're looking for doesn't exist or may have been moved.
          </motion.p>
          <motion.div variants={fadeInUp} className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/"
              data-testid="not-found-home-link"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand-blue text-white font-semibold transition-all duration-300 shadow-[0_0_20px_rgba(var(--c-purple-rgb),0.3)] hover:shadow-[0_0_40px_rgba(var(--c-purple-rgb),0.6)] hover:bg-brand-blue-light"
            >
              <Home size={18} /> Back to Home
            </Link>
            <Link
              to="/contact/"
              data-testid="not-found-contact-link"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold transition-all duration-300 text-brand-white backdrop-blur-sm"
              style={{ border: '1px solid var(--brand-border)' }}
            >
              <ArrowLeft size={18} /> Contact Us
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
